import { Helmet } from "react-helmet-async";

const BASE_URL = import.meta.env.VITE_BASE_URL || "https://rolelogic.faizo.net";

/**
 * The 1200x630 social card. The previous default here was
 * `/social-preview.png`, which is 680x240 — advertised as
 * `summary_large_image` on every SPA route and cropped to nothing by every
 * consumer that honoured the card type. `social-preview-og.png` is the same
 * asset the static pages and index.html already use.
 */
const DEFAULT_OG_IMAGE = "/social-preview-og.png";

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  noindex?: boolean;
  ogImage?: string;
  /**
   * Optional JSON-LD payload(s) to inject. Each entry becomes its own
   * <script type="application/ld+json"> tag. Use this for page-specific
   * structured data (FAQPage, BreadcrumbList, etc.) on top of the
   * site-wide Organization/SoftwareApplication tags in index.html.
   */
  jsonLd?: object | object[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = "",
  noindex = false,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
}) => {
  const canonicalUrl = `${BASE_URL}${path}`;
  const fullTitle = title
    ? `${title} | RoleLogic`
    : "RoleLogic | Discord Role Automation — Conditions & External Platforms";

  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={`${BASE_URL}${ogImage}`} />

      {/* Twitter */}
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      {description && (
        <meta name="twitter:description" content={description} />
      )}
      <meta name="twitter:image" content={`${BASE_URL}${ogImage}`} />

      {/* No hreflang alternates. Every locale is served client-side at the
          same URL, and an alternate set where all seven entries point at one
          href is not a language signal — each alternate has to be a distinct
          language-specific URL for the annotation to mean anything, and a
          self-contradictory set is discarded wholesale rather than partially
          honoured. index.html states the same decision for the homepage;
          this component used to contradict it on every other route.
          Language support is declared via WebSite.inLanguage in the
          generated entity graph instead. */}

      {jsonLdArray.map((entry, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(entry).replace(/</g, "\\u003c")}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
